<template>
  <modal
    size="xl"
    ref="modal"
    :show="show"
    @hidden="hideModal"
    :scrollable="false"
    submit-button-text="Save goal contributions"
    :hide-footer="true"
    :no-close-on-backdrop="true"
    :no-close-on-esc="true"
    modal-class="setup-goal-progress"
    :is-submitting="isSubmitting"
  >
    <div class="d-flex align-items-center" slot="modal-title">
      <i class="goal-progress-icon flex-shrink-0 mr-2"></i>
      Goal contributions
    </div>
    <highlighted-card>
      <div class="d-flex">
        <div class="d-flex flex-grow-1">
          <i class="goal-icon flex-shrink-0 main-goal mr-2"></i>
          <div class="text-normal-1 bold" v-no-capture>{{ goal.title }}</div>
        </div>
        <div class="right-section d-flex">
          <div class="team-name text-small-1 text-secondary text-truncate" :title="goaTeamTimeline">
            {{ goaTeamTimeline }}
          </div>
          <div
            class="goal-progress status-color text-normal-1 bold"
            :class="{ error: totalContribution > 100, warning: totalContribution < 100 }"
            v-if="loadingState === LoadingState.LOADED"
          >
            {{ totalContribution }}%
          </div>
        </div>
      </div>
      <div
        class="error-text status-color thin text-right text-medium-1 pt-2"
        :class="{
          show: totalContribution !== 100,
          warning: totalContribution < 100,
          error: totalContribution > 100
        }"
        v-if="loadingState === LoadingState.LOADED"
      >
        <template v-if="totalContribution > 100">
          Contributions cannot exceed 100%
        </template>
        <template v-else-if="totalContribution < 100">
          Total contributions is less than 100%
        </template>
      </div>
    </highlighted-card>
    <div v-if="loadingState === LoadingState.LOADING" class="flex-center py-5 mt-5">
      <spinner />
    </div>
    <div v-else-if="loadingState === LoadingState.ERRORED" class="py-5 text-secondary">
      Something went wrong.
    </div>
    <div v-else-if="loadingState === LoadingState.LOADED">
      <div class="text-secondary py-5 text-center" v-if="contributors.length === 0">
        This goal does not have any key results or child goals
      </div>
      <div v-else>
        <div class="table text-medium-1">
          <div class="header d-flex text-prime mb-2">
            <div class="flex-grow-1">Aligned key results and child goals</div>
            <div class="right-section d-flex">
              <div class="goal-progress">Contribution</div>
            </div>
          </div>
          <goal-contributor-row
            :source="source"
            :entity="contributor"
            :entity-type="isGoal(contributor) ? ENTITY_TYPE.GOAL : ENTITY_TYPE.KEY_RESULT"
            :key="contributor.otype + contributor.id"
            :is-fixed="checkIfFixedContributor(contributor)"
            :contribution="getContribution(contributor)"
            :open-in-edit-mode="isKR(contributor) && contributor.id === krToEdit"
            @change="onContributionChange(index, $event)"
            v-for="(contributor, index) in contributors"
          />
        </div>
        <submit-buttons
          @save="handleSubmit"
          class="side-spacing"
          submit-text="Save goal contributions"
          :is-loading="isSubmitting"
          :hide-cancel="true"
          :is-submit-disabled="!isValid"
        >
          <reset-contributions @reset="resetToDefault" :has-child-goal="hasChildGoal" />
        </submit-buttons>
        <div class="mt-4 mb-3 side-spacing">
          When new Key Results or child goals are added or removed, the contribution of items that
          are
          <b>un-locked</b> will be made equal. Child goals will not automatically contribute to the
          parent goal.
        </div>
      </div>
    </div>
  </modal>
</template>

<script>
import { trackAnalyticsEvent } from '@/analytics'
import ResetContributions from '@/components/goal/ResetContributions'
import Spinner from '@/components/Spinner'
import { EVENT_ACTION, EVENT_CATEGORY } from '@/constants/analytics'
import { LoadingState } from '@/constants/common'
import { intervalWithId } from '@/interval'
import ParentGoalsMixin from '@/mixins/ParentGoalsMixin'
import showAPIError from '@/utils/showAPIError'
import showToast from '@/utils/showToast'
import * as R from 'ramda'
import { FLEXIBLE_WEIGHTAGE } from '@/constants/goal'
import GoalContributorRow from '@/components/goal/GoalContributorRow'
import HighlightedCard from '@/components/HighlightedCard'
import Modal from '@/components/Modal'
import SubmitButtons from '@/components/SubmitButtons'
import { ENTITY_TYPE } from '@/constants/entities'
import service from '@/service'
import urlManager from '@/urlManager'
import { mapActions } from 'vuex'

/**
 * This lists down all the KRs and child goals of a goal. User can set how much each KRs and child goals can contribute to goal progress.
 */
export default {
  name: 'SetupGoalProgressModal',
  props: ['show', 'goalId', 'krToEdit', 'source'],
  model: {
    prop: 'show'
  },
  mixins: [ParentGoalsMixin],
  components: {
    Modal,
    HighlightedCard,
    GoalContributorRow,
    SubmitButtons,
    ResetContributions,
    Spinner
  },
  data() {
    return {
      loadingState: LoadingState.LOADING,
      LoadingState,
      isSubmitting: false,
      ENTITY_TYPE,
      childGoals: [],
      goalRelationships: [],
      contributors: []
    }
  },
  computed: {
    goal() {
      return this.$store.getters.goalById(this.goalId)
    },
    goalTeam() {
      const team = this.$store.getters.teamById(this.goal.teamId)
      return team.name
    },
    timeline() {
      return intervalWithId(this.goal.timeline).name
    },
    goaTeamTimeline() {
      return `${this.goalTeam} - ${this.timeline}`
    },
    keyResults() {
      return this.goal.keyResults.filter(kr => !kr.isArchived)
    },
    hasChildGoal() {
      return this.contributors.some(contr => this.isGoal(contr))
    },
    totalContribution() {
      const totalContributions = this.contributors.reduce(
        (acc, contributor) => acc + +this.getContribution(contributor),
        0
      )
      return +totalContributions.toFixed(1)
    },
    flexibleContributionAmount() {
      const flexibleContributorsCount = this.contributors.filter(
        contributor => contributor.weightage === FLEXIBLE_WEIGHTAGE
      ).length
      const fixedContributions = this.contributors
        .filter(contributor => +contributor.weightage >= 0)
        .reduce((acc, contr) => acc + +contr.weightage, 0)
      if (fixedContributions < 100)
        return +((100 - fixedContributions) / flexibleContributorsCount).toFixed(2)
      return 0
    },
    isValid() {
      return (
        !this.contributors.some(contr => R.isEmpty(contr.weightage)) &&
        this.totalContribution <= 100
      )
    }
  },
  methods: {
    ...mapActions(['getGoals', 'getGoal']),
    isGoal(contributor) {
      return !!contributor.keyResults
    },
    isKR(contributor) {
      return !this.isGoal(contributor)
    },
    resetToDefault(resetType) {
      this.contributors = this.contributors.map(contributor => ({
        ...contributor,
        weightage:
          contributor.otype === ENTITY_TYPE.KEY_RESULT || resetType === 'kr+child_goal'
            ? FLEXIBLE_WEIGHTAGE
            : 0
      }))
    },
    onContributionChange(contributorIndex, weightage) {
      const contributor = this.contributors[contributorIndex]
      this.contributors = R.update(
        contributorIndex,
        { ...contributor, weightage },
        this.contributors
      )
    },
    checkIfFixedContributor(contributor) {
      return contributor.weightage !== FLEXIBLE_WEIGHTAGE
    },
    getContribution(contributor) {
      return contributor.weightage === FLEXIBLE_WEIGHTAGE
        ? this.flexibleContributionAmount
        : contributor.weightage
    },
    async fetchChildGoals() {
      this.childGoals = await this.getGoals({
        orgId: this.goal.organizationId,
        options: {
          parentId: this.goal.id,
          isArchived: this.goal.isArchived
        }
      })
    },
    async fetchGoalRelationships() {
      const response = await service.client.get(
        urlManager.goalRelationships(this.goal.organizationId),
        {
          params: {
            parent_id: this.goal.id
          }
        }
      )
      this.goalRelationships = response.data
    },
    getGoalWeightage(goalId) {
      const relationship = this.goalRelationships.find(
        relationship => relationship.goalId === goalId
      )
      return relationship.weightage
    },
    async handleSubmit() {
      this.isSubmitting = true
      const payload = this.contributors.map(contributor => ({
        weightage:
          contributor.weightage === FLEXIBLE_WEIGHTAGE
            ? +contributor.weightage
            : +contributor.weightage / 100,
        childGoalId: contributor.otype === ENTITY_TYPE.GOAL ? contributor.id : undefined,
        keyResultId: contributor.otype === ENTITY_TYPE.KEY_RESULT ? contributor.id : undefined
      }))
      try {
        await service.client.post(
          urlManager.goalContributions({ orgId: this.goal.organizationId, goalId: this.goal.id }),
          payload
        )
        this.getGoal({
          goalId: this.goal.id,
          orgId: this.goal.organizationId,
          options: {
            expand: 'parent_goal_ids'
          }
        })
        this.refreshParentGoals(this.goal.id)
        showToast('Goal contributions have been saved successfully')
        this.hideModal()
      } catch (e) {
        showAPIError(e)
      } finally {
        this.isSubmitting = false
        this.trackSaveEvent()
      }
    },
    hideModal() {
      this.$emit('input', false)
    },
    limitTo2DecimalPlaces(number) {
      return +Number(number).toFixed(2)
    },
    trackOpenEvent() {
      trackAnalyticsEvent(EVENT_CATEGORY.NAVIGATION, EVENT_ACTION.GOAL_CONTRIBUTIONS_MODAL, {
        CTA: this.source
      })
    },
    trackSaveEvent() {
      trackAnalyticsEvent(EVENT_CATEGORY.ACTIVITY, EVENT_ACTION.SAVE_GOAL_CONTRIBUTIONS, {
        CTA: this.source
      })
    }
  },
  async mounted() {
    try {
      this.loadingState = LoadingState.LOADING
      await Promise.all([this.fetchChildGoals(), this.fetchGoalRelationships()])
      const childGoals = this.childGoals.map(childGoal => {
        const weightage = this.getGoalWeightage(childGoal.id)
        return { ...childGoal, weightage }
      })
      this.contributors = [...this.keyResults, ...childGoals].map(contributor => ({
        ...contributor,
        weightage:
          contributor.weightage === FLEXIBLE_WEIGHTAGE
            ? contributor.weightage
            : this.limitTo2DecimalPlaces(contributor.weightage * 100)
      }))
      this.loadingState = LoadingState.LOADED
    } catch (e) {
      this.loadingState = LoadingState.ERRORED
    }
    this.trackOpenEvent()
  }
}
</script>

<style lang="scss" scoped>
@import '~@/styles/_styleConfigurations.scss';
@import './styles/goalContributionModal';

.goal-progress-icon {
  @include dimension(28px, 30px);
  border: 1px solid $borderPrimary;
  border-radius: 5px;
}

.main-goal {
  margin-top: 4px;
}
.header {
  margin-top: 32px;
  padding-left: 28px;
}

.side-spacing {
  padding-left: 18px;
  padding-right: 18px;
}
.table {
  @extend .side-spacing;
  position: relative;

  &:before {
    width: 1px;
    content: ' ';
    background-color: $borderPrimary;
    top: -20px;
    bottom: 25px;
    left: 28px;
    position: absolute;
  }
}
.goal-icon {
  @include dimension(20px, 20px);
  background-size: contain;
}
.status-color {
  &.error {
    color: $error;
  }
  &.warning {
    color: $warning;
  }
}
.error-text {
  height: 0;
  transition: height ease-in 100ms;
  overflow: hidden;
  padding-right: 28px;
}
.error-text.show {
  height: 30px;
}
.team-name {
  padding-top: 6px;
}
</style>

<style lang="scss">
@import '~@/styles/_styleConfigurations.scss';
.setup-goal-progress {
  .modal-header {
    padding: 30px 110px 10px 110px;
  }
  .modal-body {
    padding: 20px 110px 0 110px;
  }
}
@include media-breakpoint-down(lg) {
  .setup-goal-progress {
    .modal-header {
      padding: 30px 30px 10px 30px;
    }
    .modal-body {
      padding: 20px 30px 0 30px;
    }
  }
}
</style>
